import React, { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import axios from "axios";
import {
  Globe2,
  LayoutDashboard,
  ListTodo,
  MessageCircle,
  UsersRound,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { API_BASE_URL } from "@/lib/apiBase";
import { useAuth } from "@/scripts/AuthContext";
import { useOrgFilter } from "@/scripts/OrgFilterContext";

/** True when a workplace DM or partner thread (not the inbox list) is open. */
export function isWorkplaceMobileThreadPath(pathname) {
  const p = String(pathname || "");
  return (
    /^\/o\/[^/]+\/messages\/\d+\/?$/.test(p) ||
    /^\/o\/[^/]+\/messages\/partner\/[^/]+\/?$/.test(p) ||
    /^\/o\/[^/]+\/p\/[^/]+\/?$/.test(p)
  );
}

export default function WorkplaceBottomNav({ orgId, className }) {
  const location = useLocation();
  const { accessToken } = useAuth();
  const { workspaceSnapshot, workspaceUpdating } = useOrgFilter();
  const [socialEnabled, setSocialEnabled] = useState(false);

  useEffect(() => {
    if (!orgId || !accessToken) {
      setSocialEnabled(false);
      return undefined;
    }
    if (workspaceUpdating) return undefined;

    if (
      workspaceSnapshot?.orgId === orgId &&
      workspaceSnapshot.socialPresence
    ) {
      setSocialEnabled(Boolean(workspaceSnapshot.socialPresence.enabled));
      return undefined;
    }

    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(
          `${API_BASE_URL}/org/${orgId}/social/presence`,
          { headers: { Authorization: `Bearer ${accessToken}` } }
        );
        if (!cancelled) {
          setSocialEnabled(Boolean(res.data?.enabled));
        }
      } catch {
        if (!cancelled) setSocialEnabled(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [orgId, accessToken, workspaceSnapshot, workspaceUpdating]);

  if (!orgId) return null;

  const enc = encodeURIComponent(String(orgId));
  const myWorkTo = `/o/${enc}/my-work`;
  const messagesTo = `/o/${enc}/messages`;
  const teamsTo = `/orgs/${enc}`;
  const socialTo = `/orgs/${enc}/social`;
  const path = location.pathname || "";

  const teamsActive =
    path === teamsTo ||
    path.startsWith(`${teamsTo}/teams/`) ||
    path.startsWith(`${teamsTo}/settings`);

  const socialActive =
    path === socialTo || path.startsWith(`${socialTo}/`);

  const items = [
    {
      id: "dashboard",
      label: "Home",
      to: "/dashboard",
      icon: LayoutDashboard,
      end: true,
    },
    {
      id: "my-work",
      label: "My work",
      to: myWorkTo,
      icon: ListTodo,
    },
    {
      id: "messages",
      label: "Messages",
      to: messagesTo,
      icon: MessageCircle,
    },
    {
      id: "teams",
      label: "Teams",
      to: teamsTo,
      icon: UsersRound,
      forceActive: teamsActive,
    },
    ...(socialEnabled
      ? [
          {
            id: "social",
            label: "Social",
            to: socialTo,
            icon: Globe2,
            forceActive: socialActive,
          },
        ]
      : []),
  ];

  return (
    <nav
      className={cn(
        "fixed inset-x-0 bottom-0 z-30 border-t border-border bg-background/95 backdrop-blur-sm md:hidden",
        className
      )}
      style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
      aria-label="Workspace"
    >
      <ul
        className={cn(
          "grid h-14",
          items.length >= 5 ? "grid-cols-5" : "grid-cols-4"
        )}
      >
        {items.map((item) => {
          const Icon = item.icon;
          return (
            <li key={item.id} className="min-w-0">
              <NavLink
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  cn(
                    "flex h-full min-w-0 flex-col items-center justify-center gap-0.5 px-0.5 text-[10px] font-medium sm:text-[11px]",
                    isActive || item.forceActive
                      ? "text-foreground"
                      : "text-muted-foreground"
                  )
                }
              >
                <Icon className="h-5 w-5 shrink-0" />
                <span className="truncate max-w-full">{item.label}</span>
              </NavLink>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
